import {
  MOBILE_USER_FACING_ERROR_MESSAGE,
  MobileApiError,
} from '@/shared/mobile-service/api/cors-mobile-api';
import type { Source } from '@/shared/types';

/** Cap a hydrate 400 so a stack trace or a dumped query cannot fill the pane. */
const MAX_HYDRATE_ERROR_CHARS = 400;

/**
 * Chat citations for the mobile-analysis agent.
 *
 * Evidence-only locators (no file path) stay out of the answer until the
 * ``mobile-analysis-agent`` flag is on. Hydrate accepts ``artifact``,
 * ``artifacts``, ``thread``, ``query``, and ``absence`` locators — not only a
 * single artifact row. Duplicate ``query`` and ``absence`` locators collapse
 * to one chip so the same result set is not listed twice; superscript clicks
 * still resolve against the original citation numbers.
 *
 * The locator may also carry ``queryShape``, ``where``, ``graphqlType``, and
 * ``highlightedArtifactIds``. Those fields are named on ``MobileEvidence`` and
 * drive the hydrated pane.
 */

type CitationLike = Pick<Source, 'sourceFilePath' | 'evidence'>;

export function hasUsableSourceFilePath(source: CitationLike): boolean {
  return Boolean(source.sourceFilePath?.trim());
}

export function isEvidenceOnlyCitation(source: CitationLike): boolean {
  return source.evidence != null && !hasUsableSourceFilePath(source);
}

/**
 * Locators ``/evidence/hydrate`` can expand into rows.
 *
 * The name says "artifact" because that was the first kind. Hydrate also
 * answers ``artifacts``, ``thread``, ``query``, and ``absence``. A ``thread``
 * locator belongs here as much as an artifact one: hydrate answers it with the
 * conversation, so leaving it out showed the raw locator for every message citation.
 */
export function isHydratableArtifactCitation(source: CitationLike): boolean {
  const kind = source.evidence?.kind;
  return (
    kind === 'artifact' ||
    kind === 'artifacts' ||
    kind === 'thread' ||
    kind === 'query' ||
    kind === 'absence'
  );
}

/** The cited ids a hydrate response points at, for highlighting inside a window. */
export function hydratedFocusIds(
  focus?: { mobileArtifactId?: number | null; mobileArtifactIds?: number[] } | null,
): number[] {
  const ids: number[] = [];
  const seen = new Set<number>();
  const add = (value: unknown) => {
    const n = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
    if (!Number.isFinite(n) || seen.has(n)) return;
    seen.add(n);
    ids.push(n);
  };
  add(focus?.mobileArtifactId);
  for (const value of focus?.mobileArtifactIds ?? []) add(value);
  return ids;
}

/**
 * The cited row inside a windowed hydrate response.
 *
 * Hydrate now returns the thread or time range around a citation, so ``items[0]``
 * is often a context row rather than the one the assistant cited. ``focus``
 * names the cited ids.
 */
export function pickHydratedArtifactItem(
  items: Record<string, unknown>[],
  focus?: { mobileArtifactId?: number | null; mobileArtifactIds?: number[] } | null,
): Record<string, unknown> | null {
  if (items.length === 0) return null;
  const ids = hydratedFocusIds(focus);
  if (ids.length > 0) {
    const wanted = new Set(ids);
    const match = items.find((item) => wanted.has(Number(item.mobileArtifactId)));
    if (match) return match;
  }
  return items[0] ?? null;
}

/** Stable identity for a citation locator. Ignores label, handle, limit, and offset. */
export function evidenceLocatorKey(evidence: CitationLike['evidence'] | undefined): string {
  if (!evidence) return '';
  return JSON.stringify({
    kind: evidence.kind,
    queryName: evidence.queryName,
    mobileAcquisitionId: evidence.mobileAcquisitionId,
    mobileArtifactId: evidence.mobileArtifactId,
    mobileArtifactIds: evidence.mobileArtifactIds,
    mobileThreadId: evidence.mobileThreadId,
    chatArtifactId: evidence.chatArtifactId,
    where: evidence.where,
    orderBy: evidence.orderBy,
    highlightedArtifactIds: evidence.highlightedArtifactIds,
    queryShape: evidence.queryShape,
    by: evidence.by,
    having: evidence.having,
    orderByCount: evidence.orderByCount,
  });
}

/**
 * One chip per query. The first citation keeps its label; later copies of the
 * same locator are dropped. Artifact rows with different ids stay separate.
 */
export function collapseDuplicateQueryCitations<T extends CitationLike>(
  citations: T[] | undefined,
): T[] | undefined {
  if (!citations) return citations;
  const seen = new Set<string>();
  let dropped = false;
  const kept: T[] = [];
  for (const source of citations) {
    const kind = source.evidence?.kind;
    if (kind !== 'query' && kind !== 'absence') {
      kept.push(source);
      continue;
    }
    const key = evidenceLocatorKey(source.evidence);
    if (seen.has(key)) {
      dropped = true;
      continue;
    }
    seen.add(key);
    kept.push(source);
  }
  return dropped ? kept : citations;
}

/** Chat source-list label: the file name, or the evidence locator label when there is no file. */
export function chatCitationLabel(source: CitationLike): string {
  const filename = source.sourceFilePath?.split('/').pop() || '';
  if (filename) return filename;
  return evidenceCitationLabel(source);
}

/**
 * Evidence-only locators stay out of the standard answer until mobile analysis is on.
 * Returns the same array reference when nothing is filtered.
 */
export function citationsVisibleInChat<T extends CitationLike>(
  citations: T[] | undefined,
  mobileEnabled: boolean,
): T[] | undefined {
  if (!citations) return citations;
  const visible = mobileEnabled
    ? citations
    : citations.filter((source) => !isEvidenceOnlyCitation(source));
  return collapseDuplicateQueryCitations(visible);
}

/**
 * Map a 1-based superscript (position in the original citation list) onto the
 * collapsed chip list from {@link citationsVisibleInChat}.
 *
 * A duplicate query locator opens the kept chip. An evidence-only row hidden
 * because mobile analysis is off is not clickable, and later numbers still
 * point at the sources that remain.
 */
export function visibleIndexForCitationSuperscript<T extends CitationLike>(
  citations: T[] | undefined,
  mobileEnabled: boolean,
  superscript: number,
): number | null {
  if (!citations || !Number.isInteger(superscript) || superscript < 1 || superscript > citations.length) {
    return null;
  }
  const original = citations[superscript - 1];
  if (!mobileEnabled && isEvidenceOnlyCitation(original)) return null;
  const visible = citationsVisibleInChat(citations, mobileEnabled) ?? [];
  const direct = visible.indexOf(original);
  if (direct >= 0) return direct;
  const kind = original.evidence?.kind;
  if (kind !== 'query' && kind !== 'absence') return null;
  const key = evidenceLocatorKey(original.evidence);
  const kept = visible.findIndex((source) => evidenceLocatorKey(source.evidence) === key);
  return kept >= 0 ? kept : null;
}

/** The selected chat source should render the mobile evidence pane instead of a file card. */
export function isMobileEvidenceSource(
  mobileEnabled: boolean,
  source: CitationLike | null | undefined,
): source is Source & { evidence: NonNullable<Source['evidence']> } {
  return Boolean(mobileEnabled && source && isEvidenceOnlyCitation(source) && source.evidence);
}

export function evidenceCitationLabel(source: CitationLike): string {
  const evidence = source.evidence;
  if (!evidence) {
    return '';
  }
  return evidence.label || evidence.artifactType || evidence.queryName || '';
}

/** Empty hydrate: name the id and query so a mistyped root is diagnosable. */
export function citationConclusion(source: CitationLike): string | null {
  const text = source.evidence?.conclusion;
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  return trimmed || null;
}

export function emptyHydratedArtifactMessage(source: CitationLike): string {
  const evidence = source.evidence;
  const label = evidence?.label?.trim();
  // A conclusion, when the model wrote one, is rendered on its own line.
  if (evidence?.kind === 'absence' || evidence?.kind === 'query') {
    return 'No results';
  }
  const id = evidence?.mobileArtifactId ?? evidence?.mobileArtifactIds?.[0];
  const query = evidence?.queryName;
  if (id != null && query) {
    return `No artifact found for ${query} ${id}`;
  }
  if (id != null) {
    return `No artifact found for artifact ${id}`;
  }
  return label || 'No artifact found';
}

/**
 * Pane copy for a failed hydrate.
 *
 * A short 400 ``detail`` (unknown group field, bad filter) is shown as-is.
 * Timeouts, 5xx bodies, and anything that looks like a traceback stay on the
 * generic message.
 */
export function citationHydrateErrorMessage(error: unknown): string {
  const detail = shortHydrateValidationDetail(error);
  return detail ?? MOBILE_USER_FACING_ERROR_MESSAGE;
}

function shortHydrateValidationDetail(error: unknown): string | null {
  if (!(error instanceof MobileApiError) || error.status !== 400 || !error.detail) {
    return null;
  }
  let text = error.detail.trim();
  if (text.startsWith('{')) {
    try {
      const parsed = JSON.parse(text) as { detail?: unknown };
      if (typeof parsed.detail !== 'string') return null;
      text = parsed.detail.trim();
    } catch {
      return null;
    }
  }
  if (!text || text.length > MAX_HYDRATE_ERROR_CHARS || text.includes('\n')) return null;
  if (/traceback|file "|stack trace/i.test(text)) return null;
  return text;
}

export function formatEvidenceJson(value: object): string {
  return JSON.stringify(value, null, 2);
}

export function hydratedEvidenceMarkdown(item: Record<string, unknown>): string | null {
  const text = item.markdown ?? item.text;
  return typeof text === 'string' ? text : null;
}

/** Ids the locator already carries; repeating them in the pane is noise. */
const HYDRATED_ARTIFACT_INTERNAL_KEYS = new Set([
  'mobileAcquisitionId',
  'mobileArtifactId',
  'mobileThreadId',
  'mobileFileId',
]);

function isEmptyFieldValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return typeof value === 'object' && Object.keys(value as object).length === 0;
}

/** Evidence text is shown verbatim, so inline markdown characters must not restyle it. */
function escapeInlineMarkdown(text: string): string {
  return text.replace(/[\\`*_[\]<]/g, (character) => `\\${character}`);
}

function formatFieldValue(value: unknown): string {
  return escapeInlineMarkdown(typeof value === 'object' ? JSON.stringify(value) : String(value));
}

function humanizeArtifactType(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }
  return value
    .replace(/[_.]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

/** Read ``fromParty.name`` out of a row whose selection set was nested. */
function readPath(row: Record<string, unknown>, path: string): unknown {
  let current: unknown = row;
  for (const segment of path.split('.')) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

function formatTimestamp(value: unknown): string {
  if (value == null || value === '') return '';
  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

/**
 * Render a hydrated thread as markdown, marking the messages the answer cited.
 *
 * ``displayFields`` is the locator's own answer for which field carries the body, the
 * sender and the timestamp. Guessing instead cannot see through a nested selection,
 * where the sender lives at ``fromParty.name`` rather than ``sender``. Returns null for
 * a thread with no authored body field so the caller can fall back to key/value.
 */
export function hydratedThreadMarkdown(
  items: Record<string, unknown>[],
  displayFields: Record<string, string> | null | undefined,
  focusIds: number[],
  label?: string,
): string | null {
  const bodyField = displayFields?.body;
  if (items.length === 0 || !bodyField) return null;

  const cited = new Set(focusIds);
  const heading = label?.trim() || 'Thread';
  const messages = items.map((item) => {
    const parts: string[] = [];
    const sender = displayFields?.sender ? readPath(item, displayFields.sender) : null;
    if (!isEmptyFieldValue(sender)) parts.push(`**${escapeInlineMarkdown(String(sender))}**`);
    const when = displayFields?.timestamp
      ? formatTimestamp(readPath(item, displayFields.timestamp))
      : '';
    if (when) parts.push(escapeInlineMarkdown(when));
    const body = readPath(item, bodyField);
    const text = isEmptyFieldValue(body) ? '' : escapeInlineMarkdown(String(body));
    // The cited message is marked rather than shown alone: the surrounding thread is
    // the point of hydrating it, and the reader still has to be told which one is cited.
    const marker = cited.has(Number(item.mobileArtifactId)) ? '▸ ' : '';
    const prefix = parts.length > 0 ? `${parts.join(' · ')}: ` : '';
    return `${marker}${prefix}${text}`;
  });

  return [`# ${heading}`, ...messages].join('\n\n');
}

/**
 * Render a hydrated artifact row as markdown. Paragraphs are separated by a blank
 * line because markdown folds a single newline into the previous line.
 */
export function hydratedArtifactMarkdown(item: Record<string, unknown>, label?: string): string {
  const heading = label?.trim() || humanizeArtifactType(item.artifactMetadataType) || 'Artifact';
  const fields = Object.entries(item)
    .filter(([key, value]) => !HYDRATED_ARTIFACT_INTERNAL_KEYS.has(key) && !isEmptyFieldValue(value))
    .map(([key, value]) => `**${key}**: ${formatFieldValue(value)}`);
  return [`# ${heading}`, ...fields].join('\n\n');
}
